import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import { IWorkflowStep } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import ApiHelper from '~/client/src/shared/utils/ApiHelper'
import { toast } from '~/client/src/shared/utils/toast'

import WorkflowTypesStore from './WorkflowTypes.store'

const TOAST_KEY = 'smart-form-import'
// The import succeeded, so this is informational — it does not need to sit there
// forever, and base-ui's toast action does not dismiss on its own.
const IMPORT_SUCCESS_TOAST_TIMEOUT = 10000
// Backstop for the failure toast. Its Dismiss button is only exposed to
// assistive tech once the viewport is focused (F6), so a keyboard-only user who
// does not know that shortcut would otherwise have no way to clear it.
const IMPORT_ERROR_TOAST_TIMEOUT = 20000

export default class SmartFormImportManagerStore {
  @observable public isImporting: boolean = false
  @observable public pendingDraft: WorkflowType = null
  @observable public importingWorkflowName: string = ''

  private abortController: AbortController = null

  public constructor(
    private readonly workflowTypesStore: WorkflowTypesStore,
    private readonly navigateToFormWorkflows: () => void,
    private readonly getValidAccessToken: () => Promise<string>,
  ) {
    makeObservable(this)
  }

  @computed
  public get hasPendingDraft(): boolean {
    return !!this.pendingDraft
  }

  public startImport = async (
    file: File,
    workflowName: string,
    categoryId: string,
  ): Promise<void> => {
    if (this.isImporting) {
      toast.error(Localization.translator.importInProgress)
      return
    }

    this.abortController = new AbortController()

    runInAction(() => {
      this.isImporting = true
      this.importingWorkflowName = workflowName
    })

    const formData = new FormData()
    formData.append('file', file)

    try {
      const accessToken = await this.getValidAccessToken()

      // No Content-Type: the browser sets the multipart boundary itself.
      const response = await fetch('/form-builder/api/v1/process', {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
        signal: this.abortController.signal,
      })

      const { data, isSuccess } = await ApiHelper.toResponseData(response)

      if (!isSuccess) {
        throw new Error(data)
      }

      this.handleImportSuccess(data.mongodb_config.WorkflowSteps, {
        workflowName,
        categoryId,
      })
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return
      }

      toast.error(
        Localization.translator.somethingWentWrongWhileImportingFile,
        {
          timeout: IMPORT_ERROR_TOAST_TIMEOUT,
          cancel: { text: Localization.translator.dismiss },
        },
      )
    } finally {
      runInAction(() => {
        this.isImporting = false
      })
      this.abortController = null
    }
  }

  @action.bound
  public consumePendingDraft(): WorkflowType {
    const draft = this.pendingDraft
    this.pendingDraft = null
    this.importingWorkflowName = ''
    toast.dismiss(TOAST_KEY)
    return draft
  }

  @action.bound
  public cancelImport(): void {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
    this.isImporting = false
    this.pendingDraft = null
    this.importingWorkflowName = ''
    toast.dismiss(TOAST_KEY)
  }

  @action
  private handleImportSuccess(
    response: IWorkflowStep[],
    params: { workflowName: string; categoryId: string },
  ): void {
    const draftType = this.workflowTypesStore.buildDraftTypeFromImport(
      params.workflowName,
      params.categoryId,
      response,
    )
    this.pendingDraft = draftType

    toast.success(
      Localization.translator.workflowWasSuccessfullyImported(
        params.workflowName,
      ),
      {
        timeout: IMPORT_SUCCESS_TOAST_TIMEOUT,
        key: TOAST_KEY,
        action: {
          text: Localization.translator.viewWorkflow,
          onClick: () => {
            toast.dismiss(TOAST_KEY)
            this.navigateToFormWorkflows()
          },
        },
      },
    )
  }
}
